import React, { useEffect, useRef, useCallback, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// ── Configuration ────────────────────────────────────────────────────────────
const IDLE_TIMEOUT_MS = 2 * 60 * 1000; // 2 min idle → show warning
const COUNTDOWN_SECONDS = 15;           // 15 s countdown before redirect
const HOME_PATH = "/";

// All events that reset the idle timer (broad list)
const IDLE_RESET_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
  "click",
];

// Only these events are valid "user gestures" for the Fullscreen API
const FULLSCREEN_GESTURE_EVENTS = ["click", "touchstart", "keydown"];

/**
 * KioskProvider
 *
 * 1. Inactivity auto-reset — after 2 min idle shows a 15-second countdown,
 *    then redirects to "/" if the customer doesn't tap "Continue".
 * 2. Fullscreen — enters fullscreen on the first qualifying user gesture
 *    (click / touchstart / keydown). Does NOT attempt fullscreen from
 *    mousemove or scroll — browsers block that with "Permissions check failed".
 */
const KioskProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const idleTimer = useRef(null);
  const countdownTimer = useRef(null);
  const hasEnteredFullscreen = useRef(false);
  const showOverlayRef = useRef(false); // mirror of state for use inside listeners

  const [showOverlay, setShowOverlay] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  // ── Fullscreen helper ──────────────────────────────────────────────────────
  // Must only be called from a direct user-gesture event handler.
  const requestFullscreen = useCallback(() => {
    if (hasEnteredFullscreen.current) return;
    const el = document.documentElement;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.mozRequestFullScreen ||
      el.msRequestFullscreen;
    if (!req) return;
    req.call(el).catch(() => {
      // Silently ignore — e.g. iframe restrictions or user denied
    });
    hasEnteredFullscreen.current = true;
  }, []);

  // ── Countdown logic ────────────────────────────────────────────────────────
  const startCountdown = useCallback(() => {
    setShowOverlay(true);
    showOverlayRef.current = true;
    setCountdown(COUNTDOWN_SECONDS);

    let remaining = COUNTDOWN_SECONDS;
    countdownTimer.current = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(countdownTimer.current);
        countdownTimer.current = null;
        setShowOverlay(false);
        showOverlayRef.current = false;
        navigate(HOME_PATH, { replace: true });
      }
    }, 1000);
  }, [navigate]);

  const cancelCountdown = useCallback(() => {
    if (countdownTimer.current) {
      clearInterval(countdownTimer.current);
      countdownTimer.current = null;
    }
    setShowOverlay(false);
    showOverlayRef.current = false;
    setCountdown(COUNTDOWN_SECONDS);
  }, []);

  // ── Idle timer ─────────────────────────────────────────────────────────────
  const resetIdleTimer = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => {
      startCountdown();
    }, IDLE_TIMEOUT_MS);
  }, [startCountdown]);

  // ── Activity listeners ─────────────────────────────────────────────────────
  useEffect(() => {
    // Generic activity → reset idle timer, dismiss overlay if showing
    const handleActivity = () => {
      if (showOverlayRef.current) cancelCountdown();
      resetIdleTimer();
    };

    // Gesture-only → request fullscreen (browsers require this)
    const handleGesture = () => {
      requestFullscreen();
    };

    IDLE_RESET_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleActivity, { passive: true })
    );
    FULLSCREEN_GESTURE_EVENTS.forEach((evt) =>
      window.addEventListener(evt, handleGesture, { passive: true })
    );

    resetIdleTimer();

    return () => {
      IDLE_RESET_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleActivity)
      );
      FULLSCREEN_GESTURE_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, handleGesture)
      );
      if (idleTimer.current) clearTimeout(idleTimer.current);
      if (countdownTimer.current) clearInterval(countdownTimer.current);
    };
  }, [resetIdleTimer, requestFullscreen, cancelCountdown]);

  // Reset idle timer on route change
  useEffect(() => {
    cancelCountdown();
    resetIdleTimer();
  }, [location.pathname, cancelCountdown, resetIdleTimer]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      {children}

      {showOverlay && (
        <div
          className="kiosk-idle-overlay"
          onTouchStart={cancelCountdown}
          onClick={cancelCountdown}
        >
          <div className="kiosk-idle-countdown">{countdown}</div>
          <div style={{ fontSize: "2rem", fontWeight: 700 }}>Still there?</div>
          <p>
            Returning to the homepage in {countdown} second
            {countdown !== 1 ? "s" : ""}…
          </p>
          <button
            className="kiosk-idle-tap-btn"
            onTouchStart={(e) => { e.stopPropagation(); cancelCountdown(); }}
            onClick={(e) => { e.stopPropagation(); cancelCountdown(); }}
          >
            Tap to Continue
          </button>
        </div>
      )}
    </>
  );
};

export default KioskProvider;
